import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Icon, Can, Button } from '@iq/react-components';

import SiteFilter from '../SiteFilter';
import SiteCreateView from '../SiteCreateView';
import SystemMessages from '../SystemMessages';
import { isFunction } from '../../utils';
import PageSettings from './PageSettings';
import config from '../../config';
import { getIsTenantOrSuperAdmin } from '../../bundles/auth';

const PageHeader = ({
  site,
  realm,
  title,
  singlePage,
  isTenantAdminView,
  apiDocumentationView,
  children,
  sitesList = false,
  setFilter = () => '',
}) => {
  const headerReference = useRef();
  const location = useLocation();
  const isTenantAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, site?.org || realm));
  const isOnPremUser = config?.onPremise && !isTenantAdmin;

  const validSite = site?.org && site?.id;
  const showSitesMap = apiDocumentationView || validSite || isTenantAdminView;
  const pathParts = location.pathname?.split('/') || [];
  const [isSettings, setIsSettings] = useState(false);

  useEffect(() => {
    window.addEventListener('scroll', () => {
      if (headerReference.current) {
        if (window.scrollY === 0 && headerReference.current.classList.contains('scroll')) {
          headerReference.current.classList.remove('scroll');
        } else if (window.scrollY > 0 && !headerReference.current.classList.contains('scroll')) {
          headerReference.current.classList.add('scroll');
        }
      }
    });
  }, []);

  const buildRoute = useCallback((index) => pathParts.slice(0, index + 4).join('/'), [pathParts]);

  const siteName = useMemo(() => {
    if (validSite) {
      return (
        <div className="site-name">
          {singlePage ? (
            <NavLink
              title={`${site.name} - Home`}
              exact
              to={() => buildRoute(-1)}
            >
              {site.name}
            </NavLink>
          ) : (
            site.name
          )}
        </div>
      );
    }
    return null;
  }, [site, validSite, singlePage]);

  const breadCrumbs = (value, index) => {
    let bcTitle;
    let bcTo;
    if (Object.hasOwn(value, 'title')) {
      bcTitle = value.title;
      bcTo = value.match.url;
    } else {
      bcTitle = value;
      bcTo = buildRoute(index);
    }
    if (index === title.value.length - 1) {
      return (
        <div
          key={`bc-${index}`}
          className="page-title--current"
        >
          {bcTitle}
        </div>
      );
    }
    return (
      <div
        key={`bc-${index}`}
        className="page-title--breadcrumb"
      >
        {isFunction(value.onClick) ? (
          <div
            title={bcTitle}
            onClick={() => {
              value.onClick();
            }}
          >
            {bcTitle}
          </div>
        ) : (
          <NavLink
            title={bcTitle}
            exact
            to={bcTo}
          >
            {bcTitle}
          </NavLink>
        )}

        <span>{'>'}</span>
      </div>
    );
  };
  const onSettings = () => {
    setIsSettings(true);
  };

  const handleCloseSettings = useCallback(() => setIsSettings(false), [setIsSettings]);

  const pageTitle = useMemo(() => {
    if (!title) {
      return null;
    }
    if (title.type === 'string') {
      return <div className="page-title">{title.value || ''}</div>;
    }
    if (title.type === 'image') {
      return (
        <img
          className="page-title"
          src={title.value}
          alt="page title"
        />
      );
    }
    if (title.type === 'breadcrumb' && title.value?.length) {
      return (
        <div className="page-title">
          {title.value.map((value, index) => breadCrumbs(value, index))}
        </div>
      );
    }
    return null;
  }, [title]);

  const sitesListClass = sitesList ? ' sites-list' : '';
  const tenantAdmin = isTenantAdminView ? 'tenant-admin' : '';

  return (
    <div
      ref={headerReference}
      className={`page-header-component${sitesListClass} ${tenantAdmin}`}
    >
      <SystemMessages />
      <div className={`page-header-content-wrapper ${config?.onPremise ? 'on-premise' : ''}`}>
        <div className={`page-header-left ${config?.onPremise ? 'on-premise' : ''}`}>
          {showSitesMap && (
            <div className="page-header-icon-wrapper">
              {!isOnPremUser && (
                <NavLink
                  title="Map"
                  exact
                  to="/sites/"
                >
                  <Icon
                    size="m"
                    icon="he-map"
                  />
                </NavLink>
              )}
            </div>
          )}
          <div className={`page-title-container${sitesListClass}`}>
            {siteName}
            <div className={'page-setting-title'}>
              {pageTitle}
              {title.index >= 0 && (
                <Can
                  permission="sites/Write"
                  scope={{ org: site?.org, site: site?.id }}
                >
                  <Button
                    className="page-setting"
                    onClick={onSettings}
                    slim
                    activity="secondary"
                    icon={<Icon icon="he-settings" />}
                    tooltip="Settings"
                  />
                  {isSettings && (
                    <PageSettings
                      site={site}
                      pageIndex={title.index}
                      onClose={handleCloseSettings}
                    />
                  )}
                </Can>
              )}
            </div>
          </div>
        </div>

        {sitesList && (
          <div className={`site-list-tools ${config?.onPremise ? 'on-premise' : ''}`}>
            {config?.onPremise ? null : <SiteFilter onChange={setFilter} />}
            <Can permission="sites/Create">
              <SiteCreateView realm={realm} />
            </Can>
          </div>
        )}

        <div className="tools">{children}</div>
      </div>
    </div>
  );
};

export default PageHeader;
